//#region src/theme-tokens.ts
/**
* Platform-agnostic canvas theme contract — the single source of truth shared
* between the host (desktop / mobile) and the iframe runtime.
*
* Nothing in this file touches the DOM, so any host can import it. The runtime
* re-exports the pure pieces from here and adds the DOM-bound helpers
* (`applyTheme`, `token`, `toneColor`) on top.
*/
const TOKEN_NAMES = [
	"background",
	"backgroundSubtle",
	"surface",
	"surfaceHover",
	"surfaceActive",
	"secondary",
	"secondaryHover",
	"secondaryActive",
	"borderSubtle",
	"border",
	"borderStrong",
	"borderStrongest",
	"foreground",
	"foregroundSubtle",
	"foregroundMuted",
	"accent",
	"accentHover",
	"accentActive",
	"accentForeground",
	"accentForegroundMuted",
	"selection",
	"success",
	"warning",
	"danger",
	"info",
	"midnightBlue",
	"claret",
	"darkEmerald",
	"cyan",
	"emerald",
	"jordyBlue",
	"plum",
	"roseQuartz",
	"red",
	"yellow"
];
const fallbackTheme = {
	mode: "light",
	tokens: {
		background: "#fbfaf5",
		backgroundSubtle: "#f8f6ee",
		surface: "#fbfaf6",
		surfaceHover: "#f2efe4",
		surfaceActive: "#edeade",
		secondary: "#edeade",
		secondaryHover: "#e8e4d6",
		secondaryActive: "#e3dece",
		borderSubtle: "#edeade",
		border: "#ece9dc",
		borderStrong: "#e3dece",
		borderStrongest: "#d5cfb8",
		foreground: "#1c1c1a",
		foregroundSubtle: "#5b594f",
		foregroundMuted: "#86847c",
		accent: "#1b1a14",
		accentHover: "#22211a",
		accentActive: "#2b2a21",
		accentForeground: "#f5f2e9",
		accentForegroundMuted: "#b8b3a1",
		selection: "#a3d2e6",
		success: "#00674f",
		warning: "#e0a500",
		danger: "#c41e3a",
		info: "#008ab3",
		midnightBlue: "#2e1f64",
		claret: "#7f1734",
		darkEmerald: "#00674f",
		cyan: "#008ab3",
		emerald: "#50c878",
		jordyBlue: "#92c2f9",
		plum: "#e89eb8",
		roseQuartz: "#a49496",
		red: "#c41e3a",
		yellow: "#ffee8c"
	}
};
const tintForDarkSurface = (color) => `color-mix(in srgb, ${color} 55%, #FFFAEB)`;
tintForDarkSurface("#00674f"), tintForDarkSurface("#c41e3a"), tintForDarkSurface("#008ab3"), tintForDarkSurface("#2e1f64"), tintForDarkSurface("#7f1734"), tintForDarkSurface("#00674f"), tintForDarkSurface("#008ab3"), tintForDarkSurface("#c41e3a");
//#endregion
//#region src/runtime/canvas-tokens.ts
const toKebabCase = (value) => value.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
const tokenVariables = Object.fromEntries(TOKEN_NAMES.map((tokenName) => [tokenName, `--lb-canvas-${toKebabCase(tokenName)}`]));
const token = Object.fromEntries(TOKEN_NAMES.map((tokenName) => [tokenName, `var(${tokenVariables[tokenName]})`]));
token.foreground, token.foregroundSubtle, token.foregroundMuted, token.accent, token.success, token.warning, token.danger, token.info;
token.accent, token.midnightBlue, token.claret, token.darkEmerald, token.cyan, token.emerald, token.jordyBlue, token.plum, token.roseQuartz, token.red, token.yellow;
token.foregroundSubtle, token.secondary, token.success, `${token.success}`, token.warning, `${token.warning}`, token.danger, `${token.danger}`, token.info, `${token.info}`, token.midnightBlue, `${token.midnightBlue}`;
const TYPE_ROLE_NAMES = [
	"sectionTitleSm",
	"sectionTitle",
	"sectionTitleMd",
	"sectionTitleLg",
	"sectionSubhead",
	"sectionAside",
	"rowTitle",
	"rowMeta",
	"rail",
	"railSm",
	"aside",
	"display",
	"displayHero",
	"displayLabel"
];
const SECTION_TITLE_TRACKING = "var(--lb-type-section-title-tracking)";
const ROLE_EXTRAS = {
	sectionTitleSm: { letterSpacing: SECTION_TITLE_TRACKING },
	sectionTitle: { letterSpacing: SECTION_TITLE_TRACKING },
	sectionTitleMd: { letterSpacing: SECTION_TITLE_TRACKING },
	sectionTitleLg: { letterSpacing: SECTION_TITLE_TRACKING },
	sectionSubhead: { letterSpacing: "var(--lb-type-section-subhead-tracking)" },
	display: { letterSpacing: "var(--lb-type-display-tracking)" },
	displayLabel: {
		letterSpacing: "var(--lb-type-display-label-tracking)",
		textTransform: "var(--lb-type-display-label-transform)"
	}
};
Object.fromEntries(TYPE_ROLE_NAMES.map((roleName) => [roleName, {
	font: `var(--lb-type-${toKebabCase(roleName)})`,
	...ROLE_EXTRAS[roleName]
}]));
`${token.foregroundMuted}`;
`${token.foregroundMuted}`;
const CANVAS_PAGE_WIDTH = 900;
const CANVAS_REGISTERS = ["plain", "warm"];
//#endregion
//#region src/package.ts
const CANVAS_FORMAT = "littlebird-canvas";
const readRegister = (parsed) => {
	if (!("register" in parsed)) return "plain";
	const declared = parsed.register;
	return CANVAS_REGISTERS.find((register) => register === declared) ?? null;
};
const readActionCards = (parsed) => {
	if (!("actionCards" in parsed)) return [];
	if (!Array.isArray(parsed.actionCards)) return null;
	const actionCards = [];
	const localIds = /* @__PURE__ */ new Set();
	for (const candidate of parsed.actionCards) {
		if (typeof candidate !== "object" || candidate === null || !("localId" in candidate) || !Number.isInteger(candidate.localId) || candidate.localId <= 0 || !("placement" in candidate) || candidate.placement !== "canvas" && candidate.placement !== "canvas-and-chat") return null;
		const localId = candidate.localId;
		if (localIds.has(localId)) return null;
		localIds.add(localId);
		actionCards.push({
			localId,
			placement: candidate.placement
		});
	}
	return actionCards;
};
const parseCanvasPackage = (packageText) => {
	let parsed;
	try {
		parsed = JSON.parse(packageText);
	} catch {
		return {
			success: false,
			error: "Canvas data is not valid JSON."
		};
	}
	if (typeof parsed !== "object" || parsed === null || !("format" in parsed) || parsed.format !== "littlebird-canvas" || !("title" in parsed) || typeof parsed.title !== "string" || "markdown" in parsed && typeof parsed.markdown !== "string" || !("source" in parsed) || typeof parsed.source !== "string" || !("code" in parsed) || typeof parsed.code !== "string") return {
		success: false,
		error: "Canvas data has an invalid shape."
	};
	const register = readRegister(parsed);
	const actionCards = readActionCards(parsed);
	if (!register || !actionCards) return {
		success: false,
		error: "Canvas data has an invalid shape."
	};
	const markdown = "markdown" in parsed && typeof parsed.markdown === "string" ? parsed.markdown : void 0;
	return {
		success: true,
		canvasPackage: {
			format: CANVAS_FORMAT,
			title: parsed.title,
			register,
			...markdown !== void 0 ? { markdown } : {},
			...actionCards.length > 0 ? { actionCards } : {},
			source: parsed.source,
			code: parsed.code
		}
	};
};
//#endregion
//#region src/content-type.ts
const CANVAS_CONTENT_TYPE = "application/vnd.littlebird.canvas+json";
const isCanvasFile = (contentType, filename) => {
	if (contentType === "application/vnd.littlebird.canvas+json") return true;
	return filename.toLowerCase().endsWith(".canvas");
};
const MAX_HTML_PREVIEW_BYTES = 8388608;
const isHtmlFile = (contentType, filename) => {
	if (contentType?.startsWith("text/html")) return true;
	const ext = filename.split(".").pop()?.toLowerCase();
	return ext === "html" || ext === "htm";
};
//#endregion
//#region src/font-licenses.ts
/**
* Caveat ships inside every canvas document as an embedded @font-face, so the OFL notice has to
* travel with those bytes rather than sit in a repo file the reader never receives. The license
* permits the notice as a human-readable header, which is what `buildFontStyles` emits.
*
* Kept verbatim from `apps/desktop/src/assets/fonts/Caveat/OFL.txt`.
*/
const CAVEAT_OFL_LICENSE = `Copyright 2014 The Caveat Project Authors (https://github.com/googlefonts/caveat)

This Font Software is licensed under the SIL Open Font License, Version 1.1.
This license is copied below, and is also available with a FAQ at:
http://scripts.sil.org/OFL


-----------------------------------------------------------
SIL OPEN FONT LICENSE Version 1.1 - 26 February 2007
-----------------------------------------------------------

PREAMBLE
The goals of the Open Font License (OFL) are to stimulate worldwide
development of collaborative font projects, to support the font creation
efforts of academic and linguistic communities, and to provide a free and
open framework in which fonts may be shared and improved in partnership
with others.

The OFL allows the licensed fonts to be used, studied, modified and
redistributed freely as long as they are not sold by themselves. The
fonts, including any derivative works, can be bundled, embedded,
redistributed and/or sold with any software provided that any reserved
names are not used by derivative works. The fonts and derivatives,
however, cannot be released under any other type of license. The
requirement for fonts to remain under this license does not apply
to any document created using the fonts or their derivatives.

DEFINITIONS
"Font Software" refers to the set of files released by the Copyright
Holder(s) under this license and clearly marked as such. This may
include source files, build scripts and documentation.

"Reserved Font Name" refers to any names specified as such after the
copyright statement(s).

"Original Version" refers to the collection of Font Software components as
distributed by the Copyright Holder(s).

"Modified Version" refers to any derivative made by adding to, deleting,
or substituting -- in part or in whole -- any of the components of the
Original Version, by changing formats or by porting the Font Software to a
new environment.

"Author" refers to any designer, engineer, programmer, technical
writer or other person who contributed to the Font Software.

PERMISSION & CONDITIONS
Permission is hereby granted, free of charge, to any person obtaining
a copy of the Font Software, to use, study, copy, merge, embed, modify,
redistribute, and sell modified and unmodified copies of the Font
Software, subject to the following conditions:

1) Neither the Font Software nor any of its individual components,
in Original or Modified Versions, may be sold by itself.

2) Original or Modified Versions of the Font Software may be bundled,
redistributed and/or sold with any software, provided that each copy
contains the above copyright notice and this license. These can be
included either as stand-alone text files, human-readable headers or
in the appropriate machine-readable metadata fields within text or
binary files as long as those fields can be easily viewed by the user.

3) No Modified Version of the Font Software may use the Reserved Font
Name(s) unless explicit written permission is granted by the corresponding
Copyright Holder. This restriction only applies to the primary font name as
presented to the users.

4) The name(s) of the Copyright Holder(s) or the Author(s) of the Font
Software shall not be used to promote, endorse or advertise any
Modified Version, except to acknowledge the contribution(s) of the
Copyright Holder(s) and the Author(s) or with their explicit written
permission.

5) The Font Software, modified or unmodified, in part or in whole,
must be distributed entirely under this license, and must not be
distributed under any other license. The requirement for fonts to
remain under this license does not apply to any document created
using the Font Software.

TERMINATION
This license becomes null and void if any of the above conditions are
not met.

DISCLAIMER
THE FONT SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO ANY WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT
OF COPYRIGHT, PATENT, TRADEMARK, OR OTHER RIGHT. IN NO EVENT SHALL THE
COPYRIGHT HOLDER BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
INCLUDING ANY GENERAL, SPECIAL, INDIRECT, INCIDENTAL, OR CONSEQUENTIAL
DAMAGES, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
FROM, OUT OF THE USE OR INABILITY TO USE THE FONT SOFTWARE OR FROM
OTHER DEALINGS IN THE FONT SOFTWARE.`;
//#endregion
//#region src/srcdoc.ts
/**
* Builds the document for a sandboxed HTML preview. `sandbox="allow-scripts"` gives an
* opaque origin (no cookies/credentials/app access) but does NOT block network, so the
* injected CSP enforces zero egress; HTML must be fully self-contained:
*   - default-src / connect-src 'none' — no fetch, XHR, WebSocket, EventSource, beacon
*   - img/media data:|blob: only — no remote exfil via element src
*   - font-src data: only
*   - script/style 'unsafe-inline' — arbitrary inline HTML/JS; safe in an opaque-origin
*     sandbox with no network/credentials
*   - form-action / base-uri 'none' — no form-POST exfil, no <base> hijack
*
* CSP cannot block document navigation (`location.href`/meta-refresh) — Chromium dropped
* `navigate-to` — so hosts must close that channel separately (Electron will-frame-navigate).
*/
const CANVAS_CSP = [
	"default-src 'none'",
	"script-src 'unsafe-inline'",
	"style-src 'unsafe-inline'",
	"img-src data: blob:",
	"font-src data:",
	"media-src data: blob:",
	"connect-src 'none'",
	"form-action 'none'",
	"base-uri 'none'"
].join("; ");
const escapeHtml = (text) => text.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll("\"", "&quot;");
const escapeInlineScript = (script) => script.replaceAll(/<\/script/gi, "<\\/script").replaceAll("\u2028", "\\u2028").replaceAll("\u2029", "\\u2029");
const serializeInlineValue = (value) => JSON.stringify(value).replaceAll("<", "\\u003c").replaceAll("\u2028", "\\u2028").replaceAll("\u2029", "\\u2029");
const buildFontStyles = (fontSources) => `
@font-face {
  font-family: "Canvas Sans";
  src: url(${JSON.stringify(fontSources.sansRegular)});
  font-style: normal;
  font-weight: 400;
  font-display: swap;
}
@font-face {
  font-family: "Canvas Sans";
  src: url(${JSON.stringify(fontSources.sansSemibold)});
  font-style: normal;
  font-weight: 600;
  font-display: swap;
}
@font-face {
  font-family: "Canvas Sans";
  src: url(${JSON.stringify(fontSources.sansBold)});
  font-style: normal;
  font-weight: 700;
  font-display: swap;
}
@font-face {
  font-family: "Canvas Serif";
  src: url(${JSON.stringify(fontSources.serifRegular)});
  font-style: normal;
  font-weight: 400;
  font-display: swap;
}
@font-face {
  font-family: "Canvas Mono";
  src: url(${JSON.stringify(fontSources.monoRegular)});
  font-style: normal;
  font-weight: 400;
  font-display: swap;
}
/*
Caveat ("Canvas Hand") is redistributed under the SIL Open Font License 1.1.

${CAVEAT_OFL_LICENSE}
*/
@font-face {
  font-family: "Canvas Hand";
  src: url(${JSON.stringify(fontSources.handRegular)});
  font-style: normal;
  font-weight: 400;
  font-display: swap;
}
`;
const errorBoundaryScript = `
function showCanvasError(reason) {
  var root = document.getElementById('root');
  if (!root) return;
  root.replaceChildren();
  var container = document.createElement('div');
  container.style.cssText = 'max-width:640px;margin:48px auto;padding:20px;border:1px solid var(--lb-canvas-border);border-radius:10px;background:var(--lb-canvas-surface);font-family:var(--lb-canvas-font-sans);color:var(--lb-canvas-foreground)';
  var title = document.createElement('div');
  title.style.cssText = 'font-size:16px;font-weight:600;margin-bottom:6px';
  title.textContent = 'This canvas could not be rendered';
  var detail = document.createElement('div');
  detail.style.cssText = 'font-size:13px;color:var(--lb-canvas-foreground-muted);white-space:pre-wrap';
  var message = reason instanceof Error ? reason.message : String(reason || 'Unknown error');
  // React #130 = "element type is invalid … got: undefined".
  if (/Minified React error #130|Element type is invalid/i.test(message)) {
    message += '\\n\\nThis usually means the canvas imported a component that is undefined (typo, non-SDK export, or missing default export), or the canvas runtime failed to expose it on globalThis.LittlebirdCanvas.';
  }
  detail.textContent = message;
  container.append(title, detail);
  root.append(container);
}
window.addEventListener('error', function (event) {
  var reason = event.error || event.message;
  var message = reason instanceof Error ? reason.message : String(reason || '');
  if (message === 'ResizeObserver loop limit exceeded' || message === 'ResizeObserver loop completed with undelivered notifications.') return;
  showCanvasError(reason);
});
window.addEventListener('unhandledrejection', function (event) { showCanvasError(event.reason); });
`;
/**
* Runs after the runtime IIFE and before canvas code so a broken/missing runtime
* surfaces a clear error instead of React's minified #130 ("got: undefined").
*/
const runtimePreflightScript = `
(function () {
  var lc = globalThis.LittlebirdCanvas;
  if (!lc) {
    throw new Error(
      'Canvas runtime did not initialize (globalThis.LittlebirdCanvas is missing). Rebuild @littlebird/canvas and restart the host.'
    );
  }
  var required = ['React', 'ReactDOMClient', 'jsxRuntime'];
  var missing = [];
  for (var i = 0; i < required.length; i++) {
    if (lc[required[i]] == null) missing.push(required[i]);
  }
  if (missing.length) {
    throw new Error(
      'Canvas runtime is incomplete. Missing required: [' + missing.join(', ') + '].'
    );
  }
  if (typeof lc.React.createElement !== 'function') {
    throw new Error('Canvas runtime React export is malformed (createElement missing).');
  }
  if (typeof lc.ReactDOMClient.createRoot !== 'function') {
    throw new Error('Canvas runtime ReactDOMClient export is malformed (createRoot missing).');
  }
  if (typeof lc.jsxRuntime.jsx !== 'function') {
    throw new Error('Canvas runtime jsxRuntime export is malformed (jsx missing).');
  }
})();
`;
const LEADING_DOCTYPE = /^\s*<!doctype[^>]*>/i;
/**
* Lead with a doctype (standards mode), then the CSP `<meta>` and optional host
* script, then the body. A doctype runs no code, so leading with it keeps CSP
* parsed before any untrusted `<script>` (the security invariant) while avoiding
* quirks mode — a `<meta>`/`<script>` ahead of the doctype would force it. The
* parser hoists the leading `<meta>` into the synthesized head, so a
* document-leading CSP still governs the page.
*/
const buildSandboxedSrcDoc = (html, { headScript, headStyle } = {}) => {
	const cspMetaTag = `<meta http-equiv="Content-Security-Policy" content="${CANVAS_CSP}">`;
	const headScriptTag = headScript ? `<script>${escapeInlineScript(headScript)}<\/script>` : "";
	const headStyleTag = headStyle ? `<style>${headStyle.replace(/<\/style/gi, "<\\/style")}</style>` : "";
	const leadingDoctype = html.match(LEADING_DOCTYPE);
	return `${leadingDoctype ? leadingDoctype[0] : "<!doctype html>"}${cspMetaTag}${headScriptTag}${headStyleTag}${leadingDoctype ? html.slice(leadingDoctype[0].length) : html}`;
};
const buildCanvasSrcDoc = ({ canvasPackage, hostTheme, runtimeSource, fontSources, headScript }) => {
	if (runtimeSource.length === 0) throw new Error("buildCanvasSrcDoc requires a non-empty runtimeSource string. Import from @littlebird/canvas/runtime-bundle after building the package.");
	const html = `<!doctype html>
<html lang="en" data-theme="${hostTheme.mode}" data-register="${canvasPackage.register}">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(canvasPackage.title)}</title>
  <style>${buildFontStyles(fontSources)}</style>
</head>
<body>
  <div id="root"></div>
  <script>globalThis.littlebirdCanvasHostTheme = ${serializeInlineValue(hostTheme)};<\/script>
  <script>${escapeInlineScript(errorBoundaryScript)}<\/script>
  <script>${escapeInlineScript(runtimeSource)}<\/script>
  <script>${escapeInlineScript(runtimePreflightScript)}<\/script>
  <script>${escapeInlineScript(canvasPackage.code)}<\/script>
</body>
</html>`;
	return buildSandboxedSrcDoc(html, { headScript });
};
//#endregion
//#region src/chat-prompt.ts
const CANVAS_CHAT_PROMPT_MESSAGE_TYPE = "littlebird-canvas-chat-prompt";
const CANVAS_CHAT_PROMPT_ATTRIBUTE = "data-littlebird-chat-prompt";
/**
* A canvas is untrusted content, so the host caps what it can put in the composer. Longer than any
* follow-up question a canvas has business offering.
*/
const MAX_CANVAS_CHAT_PROMPT_LENGTH = 2e3;
const createCanvasChatPromptCapability = () => {
	const randomBytes = /* @__PURE__ */ new Uint8Array(16);
	if (globalThis.crypto?.getRandomValues) {
		globalThis.crypto.getRandomValues(randomBytes);
		return Array.from(randomBytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
	}
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};
const normalizeCanvasChatPrompt = (prompt) => {
	if (typeof prompt !== "string") return null;
	const normalizedPrompt = prompt.trim();
	if (!normalizedPrompt || normalizedPrompt.length > 2e3) return null;
	return normalizedPrompt;
};
const parseCanvasChatPromptMessage = (data, expectedCapability) => {
	if (!data || typeof data !== "object") return null;
	const message = data;
	if (message.type !== "littlebird-canvas-chat-prompt" || message.capability !== expectedCapability) return null;
	return normalizeCanvasChatPrompt(message.prompt);
};
const buildCanvasChatPromptBridgeScript = ({ capability }) => `
(() => {
  const capability = ${JSON.stringify(capability)};
  document.currentScript?.remove();
  globalThis.littlebirdCanvasHostCapabilities = {
    ...globalThis.littlebirdCanvasHostCapabilities,
    chatPrompt: true
  };

  document.addEventListener('click', function (event) {
    if (!event.isTrusted) return;
    const clicked = event.target instanceof Element ? event.target : null;
    const action = clicked && clicked.closest('[${CANVAS_CHAT_PROMPT_ATTRIBUTE}]');
    if (!action) return;
    const prompt = (action.getAttribute('${CANVAS_CHAT_PROMPT_ATTRIBUTE}') || '').trim();
    if (!prompt || prompt.length > ${MAX_CANVAS_CHAT_PROMPT_LENGTH}) return;

    const message = {
      type: '${CANVAS_CHAT_PROMPT_MESSAGE_TYPE}',
      prompt,
      capability
    };
    window.parent.postMessage(message, '*');
  }, true);
})();
`;
//#endregion
//#region src/action-card.ts
const CANVAS_ACTION_CARD_SLOTS_MESSAGE_TYPE = "littlebird-canvas-action-card-slots";
const CANVAS_ACTION_CARD_HEIGHT_MESSAGE_TYPE = "littlebird-canvas-action-card-height";
const CANVAS_ACTION_CARD_SLOT_ATTRIBUTE = "data-littlebird-action-card-slot";
const CANVAS_ACTION_CARD_LOCAL_ID_ATTRIBUTE = "data-littlebird-action-card-local-id";
const MAX_CANVAS_ACTION_CARD_SLOTS = 20;
const MIN_CANVAS_ACTION_CARD_SLOT_WIDTH = 320;
const MIN_CANVAS_ACTION_CARD_SLOT_HEIGHT = 44;
const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);
const isCanvasActionCardSlotGeometrySafe = (slot, bounds) => isFiniteNumber(bounds.width) && isFiniteNumber(bounds.height) && bounds.width > 0 && bounds.height > 0 && slot.left >= 0 && slot.top >= 0 && slot.width >= MIN_CANVAS_ACTION_CARD_SLOT_WIDTH && slot.height >= MIN_CANVAS_ACTION_CARD_SLOT_HEIGHT && slot.left + slot.width <= bounds.width && slot.top + slot.height <= bounds.height;
const parseCanvasActionCardSlotsMessage = (data, expectedCapability, allowedLocalIds, bounds) => {
	if (!data || typeof data !== "object") return null;
	const message = data;
	if (message.type !== "littlebird-canvas-action-card-slots" || message.capability !== expectedCapability || !Array.isArray(message.slots) || message.slots.length > MAX_CANVAS_ACTION_CARD_SLOTS) return null;
	const slots = [];
	const localIds = /* @__PURE__ */ new Set();
	const slotIds = /* @__PURE__ */ new Set();
	for (const candidate of message.slots) {
		if (!candidate || typeof candidate !== "object") return null;
		const slot = candidate;
		if (typeof slot.slotId !== "string" || !slot.slotId || !Number.isInteger(slot.localId) || !allowedLocalIds.has(slot.localId) || !isFiniteNumber(slot.top) || !isFiniteNumber(slot.left) || !isFiniteNumber(slot.width) || !isFiniteNumber(slot.height) || slot.width <= 0 || slot.height <= 0) return null;
		if (!isCanvasActionCardSlotGeometrySafe(slot, bounds)) return null;
		if (localIds.has(slot.localId) || slotIds.has(slot.slotId)) return null;
		localIds.add(slot.localId);
		slotIds.add(slot.slotId);
		slots.push(slot);
	}
	return slots;
};
const ACTION_CARD_CAPABILITY_PLACEHOLDER = "__LITTLEBIRD_ACTION_CARD_CAPABILITY__";
const CANVAS_ACTION_CARD_BRIDGE_SCRIPT = `
(() => {
  const capability = ${ACTION_CARD_CAPABILITY_PLACEHOLDER};
  const postToParent = window.parent.postMessage.bind(window.parent);
  document.currentScript?.remove();
  globalThis.littlebirdCanvasHostCapabilities = {
    ...globalThis.littlebirdCanvasHostCapabilities,
    actionCard: true
  };
  let reportFrame = 0;

  const reportSlots = () => {
    reportFrame = 0;
    const slots = Array.from(
      document.querySelectorAll('[${CANVAS_ACTION_CARD_SLOT_ATTRIBUTE}]')
    ).slice(0, ${MAX_CANVAS_ACTION_CARD_SLOTS}).flatMap((element) => {
      const slotId = element.getAttribute('${CANVAS_ACTION_CARD_SLOT_ATTRIBUTE}') || '';
      const localId = Number(element.getAttribute('${CANVAS_ACTION_CARD_LOCAL_ID_ATTRIBUTE}'));
      const rect = element.getBoundingClientRect();
      if (!slotId || !Number.isInteger(localId) || rect.width <= 0 || rect.height <= 0) {
        return [];
      }
      return [{
        slotId,
        localId,
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      }];
    });
    postToParent({
      type: '${CANVAS_ACTION_CARD_SLOTS_MESSAGE_TYPE}',
      capability,
      slots
    }, '*');
  };

  const scheduleReport = () => {
    if (reportFrame) cancelAnimationFrame(reportFrame);
    reportFrame = requestAnimationFrame(reportSlots);
  };

  window.addEventListener('message', (event) => {
    const message = event.data;
    if (
      event.source !== window.parent ||
      message?.type !== '${CANVAS_ACTION_CARD_HEIGHT_MESSAGE_TYPE}' ||
      message.capability !== capability ||
      typeof message.slotId !== 'string' ||
      !Number.isFinite(message.height)
    ) {
      return;
    }
    const slot = Array.from(
      document.querySelectorAll('[${CANVAS_ACTION_CARD_SLOT_ATTRIBUTE}]')
    ).find(
      (element) =>
        element.getAttribute('${CANVAS_ACTION_CARD_SLOT_ATTRIBUTE}') === message.slotId
    );
    if (!slot) return;
    slot.style.setProperty('--lb-canvas-action-card-height', Math.max(1, Math.ceil(message.height)) + 'px');
    scheduleReport();
  });

  window.addEventListener('DOMContentLoaded', () => {
    const observer = new ResizeObserver(scheduleReport);
    observer.observe(document.documentElement);
    const root = document.getElementById('root');
    if (root) observer.observe(root);
    new MutationObserver(scheduleReport).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
    scheduleReport();
  }, { once: true });
  window.addEventListener('resize', scheduleReport);
  document.addEventListener('scroll', scheduleReport, true);
})();
`;
const buildCanvasActionCardBridgeScript = (capability) => CANVAS_ACTION_CARD_BRIDGE_SCRIPT.replace(ACTION_CARD_CAPABILITY_PLACEHOLDER, JSON.stringify(capability));
//#endregion
//#region src/runtime/canvas-brand-marks.ts
const BRAND_MARKS = {
	gmail: {
		label: "Gmail",
		viewBox: "0 0 24 24",
		paths: ["M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"],
		artwork: {
			viewBox: "3 6.619 26 19.5",
			paths: [
				{
					d: "M4.77273 26.1191H8.90909V16.0737L3 11.6419V24.3464C3 25.3273 3.79477 26.1191 4.77273 26.1191Z",
					fill: "#4285F4"
				},
				{
					d: "M23.0909 26.1191H27.2273C28.2082 26.1191 29 25.3244 29 24.3464V11.6419L23.0909 16.0737",
					fill: "#34A853"
				},
				{
					d: "M23.0909 8.39185V16.0737L29 11.6418V9.27821C29 7.08594 26.4975 5.83617 24.7455 7.15094",
					fill: "#FBBC04"
				},
				{
					d: "M8.9091 16.0737V8.39185L16 13.71L23.0909 8.39185V16.0737L16 21.3919",
					fill: "#EA4335"
				},
				{
					d: "M3 9.27804V11.6417L8.90909 16.0735V8.39168L7.25455 7.15077C5.49955 5.836 3 7.08577 3 9.27804Z",
					fill: "#C5221F"
				}
			]
		}
	},
	"google-calendar": {
		label: "Google Calendar",
		viewBox: "0 0 24 24",
		paths: ["M18.316 5.684H24v12.632h-5.684V5.684zM5.684 24h12.632v-5.684H5.684V24zM18.316 5.684V0H1.895A1.894 1.894 0 0 0 0 1.895v16.421h5.684V5.684h12.632zm-7.207 6.25v-.065c.272-.144.5-.349.687-.617s.279-.595.279-.982c0-.379-.099-.72-.3-1.025a2.05 2.05 0 0 0-.832-.714 2.703 2.703 0 0 0-1.197-.257c-.6 0-1.094.156-1.481.467-.386.311-.65.671-.793 1.078l1.085.452c.086-.249.224-.461.413-.633.189-.172.445-.257.767-.257.33 0 .602.088.816.264a.86.86 0 0 1 .322.703c0 .33-.12.589-.36.778-.24.19-.535.284-.886.284h-.567v1.085h.633c.407 0 .748.109 1.02.327.272.218.407.499.407.843 0 .336-.129.614-.387.832s-.565.327-.924.327c-.351 0-.651-.103-.897-.311-.248-.208-.422-.502-.521-.881l-1.096.452c.178.616.505 1.082.977 1.401.472.319.984.478 1.538.477a2.84 2.84 0 0 0 1.293-.291c.382-.193.684-.458.902-.794.218-.336.327-.72.327-1.149 0-.429-.115-.797-.344-1.105a2.067 2.067 0 0 0-.881-.689zm2.093-1.931l.602.913L15 10.045v5.744h1.187V8.446h-.827l-2.158 1.557zM22.105 0h-3.289v5.184H24V1.895A1.894 1.894 0 0 0 22.105 0zm-3.289 23.5l4.684-4.684h-4.684V23.5zM0 22.105C0 23.152.848 24 1.895 24h3.289v-5.184H0v3.289z"],
		artwork: {
			viewBox: "4 4.35 24 24",
			paths: [
				{
					d: "M22.3158 10.0342L16.6317 9.4026L9.68428 10.0342L9.0526 16.35L9.68416 22.6658L16 23.4553L22.3158 22.6658L22.9474 16.1922L22.3158 10.0342Z",
					fill: "#FFFFFF"
				},
				{
					d: "M12.2753 19.8331C11.8032 19.5142 11.4764 19.0484 11.2979 18.4326L12.3938 17.981C12.4932 18.36 12.6669 18.6536 12.9148 18.8621C13.1612 19.0705 13.4612 19.1731 13.8117 19.1731C14.1701 19.1731 14.478 19.0642 14.7353 18.8462C14.9926 18.6283 15.1222 18.3504 15.1222 18.0142C15.1222 17.67 14.9864 17.3888 14.7148 17.171C14.4432 16.9532 14.1022 16.8442 13.6948 16.8442H13.0617V15.7595H13.63C13.9805 15.7595 14.2758 15.6648 14.5158 15.4753C14.7558 15.2858 14.8758 15.0269 14.8758 14.6969C14.8758 14.4032 14.7684 14.1695 14.5538 13.9943C14.3391 13.8191 14.0674 13.7306 13.7374 13.7306C13.4153 13.7306 13.1595 13.816 12.97 13.988C12.7805 14.1601 12.6431 14.3717 12.5564 14.6212L11.4717 14.1696C11.6153 13.7622 11.8791 13.4022 12.2658 13.0912C12.6527 12.7801 13.1469 12.6238 13.7469 12.6238C14.1905 12.6238 14.59 12.7091 14.9438 12.8812C15.2974 13.0532 15.5753 13.2917 15.7758 13.5948C15.9764 13.8995 16.0758 14.2406 16.0758 14.6195C16.0758 15.0064 15.9827 15.3331 15.7964 15.6016C15.61 15.87 15.381 16.0752 15.1095 16.219V16.2836C15.4679 16.4336 15.76 16.6626 15.9905 16.9705C16.2195 17.2784 16.3347 17.6464 16.3347 18.0758C16.3347 18.5053 16.2257 18.889 16.0078 19.2253C15.7899 19.5617 15.4883 19.8269 15.1062 20.0195C14.7226 20.2121 14.2916 20.31 13.8131 20.31C13.259 20.3116 12.7474 20.1521 12.2753 19.8331Z",
					fill: "#1A73E8"
				},
				{
					d: "M19 14.3953L17.8031 15.2653L17.2016 14.3527L19.36 12.7958H20.1874V20.1395H19V14.3953Z",
					fill: "#1A73E8"
				},
				{
					d: "M22.3158 28.35L28 22.6658L25.1579 21.4027L22.3158 22.6658L21.0527 25.5079L22.3158 28.35Z",
					fill: "#EA4335"
				},
				{
					d: "M8.42104 25.5079L9.68416 28.35H22.3157V22.6658H9.68416L8.42104 25.5079Z",
					fill: "#34A853"
				},
				{
					d: "M5.89468 4.35C4.84792 4.35 4 5.19792 4 6.24468V22.6657L6.84208 23.9288L9.68416 22.6657V10.0342H22.3157L23.5788 7.19208L22.3158 4.35H5.89468Z",
					fill: "#4285F4"
				},
				{
					d: "M4 22.6658V26.4553C4 27.5022 4.84792 28.35 5.89468 28.35H9.68416V22.6658H4Z",
					fill: "#188038"
				},
				{
					d: "M22.3158 10.0342V22.6657H28V10.0342L25.1579 8.77105L22.3158 10.0342Z",
					fill: "#FBBC04"
				},
				{
					d: "M28 10.0342V6.24468C28 5.1978 27.1521 4.35 26.1053 4.35H22.3158V10.0342H28Z",
					fill: "#1967D2"
				}
			]
		}
	},
	"google-drive": {
		label: "Google Drive",
		viewBox: "0 0 24 24",
		paths: ["M12.01 1.485c-2.082 0-3.754.02-3.743.047.01.02 1.708 3.001 3.774 6.62l3.76 6.574h3.76c2.081 0 3.753-.02 3.742-.047-.005-.02-1.708-3.001-3.775-6.62l-3.76-6.574zm-4.76 1.73a789.828 789.861 0 0 0-3.63 6.319L0 15.868l1.89 3.298 1.885 3.297 3.62-6.335 3.618-6.33-1.88-3.287C8.1 4.704 7.255 3.22 7.25 3.214zm2.259 12.653-.203.348c-.114.198-.96 1.672-1.88 3.287a423.93 423.948 0 0 1-1.698 2.97c-.01.026 3.24.042 7.222.042h7.244l1.796-3.157c.992-1.734 1.85-3.23 1.906-3.323l.104-.167h-7.249z"],
		artwork: {
			viewBox: "3 4.735 26 23.23",
			paths: [
				{
					d: "M4.96564 24.6444L6.11226 26.6249C6.35052 27.0419 6.69301 27.3695 7.09507 27.6077L11.1901 20.5195H3C3 20.9812 3.11913 21.4428 3.35739 21.8597L4.96564 24.6444Z",
					fill: "#0066DA"
				},
				{
					d: "M16 12.1805L11.9049 5.09226C11.5029 5.33052 11.1604 5.65813 10.9221 6.07508L3.35739 19.1793C3.12351 19.5873 3.00031 20.0493 3 20.5195H11.1901L16 12.1805Z",
					fill: "#00AC47"
				},
				{
					d: "M24.9049 27.6077C25.307 27.3695 25.6495 27.0419 25.8877 26.6249L26.3643 25.8059L28.6426 21.8597C28.8809 21.4428 29 20.9812 29 20.5195H20.8093L22.5521 23.9445L24.9049 27.6077Z",
					fill: "#EA4335"
				},
				{
					d: "M16 12.1805L20.0951 5.09227C19.693 4.85401 19.2314 4.73488 18.7549 4.73488H13.2451C12.7686 4.73488 12.307 4.8689 11.9049 5.09227L16 12.1805Z",
					fill: "#00832D"
				},
				{
					d: "M20.8099 20.5195H11.1901L7.09507 27.6077C7.49714 27.846 7.95876 27.9651 8.43528 27.9651H23.5647C24.0412 27.9651 24.5029 27.8311 24.9049 27.6077L20.8099 20.5195Z",
					fill: "#2684FC"
				},
				{
					d: "M24.8603 12.6272L21.0779 6.07508C20.8396 5.65813 20.4971 5.33052 20.0951 5.09226L16 12.1805L20.8099 20.5195H28.9851C28.9851 20.0579 28.866 19.5963 28.6277 19.1793L24.8603 12.6272Z",
					fill: "#FFBA00"
				}
			]
		}
	},
	dropbox: {
		label: "Dropbox",
		viewBox: "0 0 24 24",
		paths: ["M6 1.807L0 5.629l6 3.822 6.001-3.822L6 1.807zM18 1.807l-6 3.822 6 3.822 6-3.822-6-3.822zM0 13.274l6 3.822 6.001-3.822L6 9.452l-6 3.822zM18 9.452l-6 3.822 6 3.822 6-3.822-6-3.822zM6 18.371l6.001 3.822 6-3.822-6-3.822L6 18.371z"],
		hex: "#0061FF"
	},
	slack: {
		label: "Slack",
		viewBox: "0 0 127 127",
		paths: [
			"M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80zm6.6 0c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z",
			"M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47zm0 6.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H13.9C6.6 60.1.7 54.2.7 46.9c0-7.3 5.9-13.2 13.2-13.2H47z",
			"M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9zm-6.6 0c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.9 6.5 72.8.6 80.1.6c7.3 0 13.2 5.9 13.2 13.2v33.1z",
			"M80.1 99.8c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.8h13.2zm0-6.6c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80.1z"
		],
		artwork: {
			viewBox: "0 0 127 127",
			paths: [
				{
					d: "M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80zm6.6 0c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z",
					fill: "#E01E5A"
				},
				{
					d: "M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47zm0 6.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H13.9C6.6 60.1.7 54.2.7 46.9c0-7.3 5.9-13.2 13.2-13.2H47z",
					fill: "#36C5F0"
				},
				{
					d: "M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9zm-6.6 0c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.9 6.5 72.8.6 80.1.6c7.3 0 13.2 5.9 13.2 13.2v33.1z",
					fill: "#2EB67D"
				},
				{
					d: "M80.1 99.8c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.8h13.2zm0-6.6c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80.1z",
					fill: "#ECB22E"
				}
			]
		}
	},
	notion: {
		label: "Notion",
		viewBox: "0 0 24 24",
		paths: ["M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.981-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.887l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952L12.21 19s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.139c-.093-.514.28-.887.747-.933zM1.936 1.035l13.31-.98c1.634-.14 2.055-.047 3.082.7l4.249 2.986c.7.513.934.653.934 1.213v16.378c0 1.026-.373 1.634-1.68 1.726l-15.458.934c-.98.047-1.448-.093-1.962-.747l-3.129-4.06c-.56-.747-.793-1.306-.793-1.96V2.667c0-.839.374-1.54 1.447-1.632z"],
		hex: "#000000"
	},
	linear: {
		label: "Linear",
		viewBox: "0 0 24 24",
		paths: ["M2.886 4.18A11.982 11.982 0 0 1 11.99 0C18.624 0 24 5.376 24 12.009c0 3.64-1.62 6.903-4.18 9.105L2.887 4.18ZM1.817 5.626l16.556 16.556c-.524.33-1.075.62-1.65.866L.951 7.277c.247-.575.537-1.126.866-1.65ZM.322 9.163l14.515 14.515c-.71.172-1.443.282-2.195.322L0 11.358a12 12 0 0 1 .322-2.195Zm-.17 4.862 9.823 9.824a12.02 12.02 0 0 1-9.824-9.824Z"],
		hex: "#5E6AD2"
	},
	github: {
		label: "GitHub",
		viewBox: "0 0 24 24",
		paths: ["M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"],
		hex: "#181717"
	},
	asana: {
		label: "Asana",
		viewBox: "0 0 24 24",
		paths: ["M18.78 12.653c-2.882 0-5.22 2.336-5.22 5.22s2.338 5.22 5.22 5.22 5.22-2.34 5.22-5.22-2.336-5.22-5.22-5.22zm-13.56 0c-2.88 0-5.22 2.337-5.22 5.22s2.338 5.22 5.22 5.22 5.22-2.338 5.22-5.22-2.336-5.22-5.22-5.22zm12-6.525c0 2.883-2.337 5.22-5.22 5.22-2.882 0-5.22-2.337-5.22-5.22 0-2.88 2.338-5.22 5.22-5.22 2.883 0 5.22 2.34 5.22 5.22z"],
		hex: "#F06A6A"
	},
	hubspot: {
		label: "HubSpot",
		viewBox: "0 0 24 24",
		paths: ["M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.067A2.2 2.2 0 0017.238.845h-.067a2.2 2.2 0 00-2.193 2.193v.067a2.196 2.196 0 001.252 1.973l.013.006v2.852a6.22 6.22 0 00-2.969 1.31l.012-.01-7.828-6.095A2.497 2.497 0 104.3 4.656l-.012.006 7.697 5.991a6.176 6.176 0 00-1.038 3.446c0 1.343.425 2.588 1.147 3.607l-.013-.02-2.342 2.343a1.968 1.968 0 00-.58-.095h-.002a2.033 2.033 0 102.033 2.033 1.978 1.978 0 00-.1-.595l.005.014 2.317-2.317a6.247 6.247 0 104.782-11.134l-.036-.005zm-.964 9.378a3.206 3.206 0 113.215-3.207v.002a3.206 3.206 0 01-3.207 3.207z"],
		hex: "#FF7A59"
	},
	zoom: {
		label: "Zoom",
		viewBox: "0 0 24 24",
		paths: ["M5.033 14.649H.743a.74.74 0 0 1-.686-.458.74.74 0 0 1 .16-.808L3.19 10.41H1.06A1.06 1.06 0 0 1 0 9.35h3.957c.301 0 .57.18.686.458a.74.74 0 0 1-.161.808L1.51 13.59h2.464c.585 0 1.06.475 1.06 1.06zM24 11.338c0-1.14-.927-2.066-2.066-2.066-.61 0-1.158.265-1.537.686a2.061 2.061 0 0 0-1.536-.686c-1.14 0-2.066.926-2.066 2.066v3.311a1.06 1.06 0 0 0 1.06-1.06v-2.251a1.004 1.004 0 0 1 2.013 0v2.251c0 .586.474 1.06 1.06 1.06v-3.311a1.004 1.004 0 0 1 2.012 0v2.251c0 .586.475 1.06 1.06 1.06zM16.265 12a2.728 2.728 0 1 1-5.457 0 2.728 2.728 0 0 1 5.457 0zm-1.06 0a1.669 1.669 0 1 0-3.338 0 1.669 1.669 0 0 0 3.338 0zm-4.82 0a2.728 2.728 0 1 1-5.458 0 2.728 2.728 0 0 1 5.457 0zm-1.06 0a1.669 1.669 0 1 0-3.338 0 1.669 1.669 0 0 0 3.338 0z"],
		hex: "#0B5CFF"
	},
	"google-meet": {
		label: "Google Meet",
		viewBox: "0 0 24 24",
		paths: ["M5.53 2.13 0 7.75h5.53zm.398 0v5.62h7.608v3.65l5.47-4.45c-.014-1.22.031-2.25-.025-3.46-.148-1.09-1.287-1.47-2.236-1.36zM23.1 4.32c-.802.295-1.358.995-2.047 1.49-2.506 2.05-4.982 4.12-7.468 6.19 3.025 2.59 6.04 5.18 9.065 7.76 1.218.671 1.428-.814 1.328-1.64v-13a.828.828 0 0 0-.877-.825zM.038 8.15v7.7h5.53v-7.7zm13.577 8.1H6.008v5.62c3.864-.006 7.737.011 11.58-.009 1.02-.07 1.618-1.12 1.468-2.07v-2.51l-5.47-4.68v3.65zm-13.577 0c.02 1.44-.041 2.88.033 4.31.162.948 1.158 1.43 2.047 1.31h3.464v-5.62z"],
		hex: "#00832D"
	}
};
//#endregion
export { BRAND_MARKS, CANVAS_ACTION_CARD_HEIGHT_MESSAGE_TYPE, CANVAS_ACTION_CARD_LOCAL_ID_ATTRIBUTE, CANVAS_ACTION_CARD_SLOTS_MESSAGE_TYPE, CANVAS_ACTION_CARD_SLOT_ATTRIBUTE, CANVAS_CHAT_PROMPT_ATTRIBUTE, CANVAS_CHAT_PROMPT_MESSAGE_TYPE, CANVAS_CONTENT_TYPE, CANVAS_FORMAT, CANVAS_PAGE_WIDTH, MAX_CANVAS_CHAT_PROMPT_LENGTH, MAX_HTML_PREVIEW_BYTES, TOKEN_NAMES, buildCanvasActionCardBridgeScript, buildCanvasChatPromptBridgeScript, buildCanvasSrcDoc, buildSandboxedSrcDoc, createCanvasChatPromptCapability, fallbackTheme, isCanvasActionCardSlotGeometrySafe, isCanvasFile, isHtmlFile, normalizeCanvasChatPrompt, parseCanvasActionCardSlotsMessage, parseCanvasChatPromptMessage, parseCanvasPackage };

//# sourceMappingURL=index.js.map